import React, { Component } from "react";
import ReactDOM from "react-dom";
import sysJwt from "../../../utils/sysJwt";
import { post } from "../../../utils/sysAxios";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { withTranslation } from "react-i18next";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

/* ── Overlay ── */
.tdm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  backdrop-filter: blur(3px);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: tdm-fade-in .15s ease;
}
@keyframes tdm-fade-in { from { opacity: 0; } to { opacity: 1; } }

/* Force PrimeReact dropdown panels (appended to body) above the modal overlay */
body > .p-dropdown-panel {
  z-index: 9999999 !important;
}

/* ── Modal shell ── */
.tdm-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,.25);
  animation: tdm-slide-up .2s ease;
  font-family: 'DM Sans', sans-serif;
}
@keyframes tdm-slide-up {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Modal header ── */
.tdm-header {
  background: #1A1D23;
  padding: 18px 22px 0;
  flex-shrink: 0;
}
.tdm-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.tdm-ticket-id {
  font-family: 'DM Mono', monospace;
  font-size: 0.72rem;
  color: #6B7280;
  margin-bottom: 2px;
}
.tdm-ticket-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.35;
}
.tdm-close-btn {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1.5px solid #374151;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all .15s;
  font-size: 0.9rem;
}
.tdm-close-btn:hover { border-color: #EF4444; color: #EF4444; background: rgba(239,68,68,.1); }

/* ── Meta badges ── */
.tdm-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.tdm-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.1);
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  color: #D1D5DB;
}
.tdm-meta-chip i { opacity: .7; }
.tdm-meta-chip.status-open    { border-color: #3B82F6; color: #93C5FD; }
.tdm-meta-chip.status-inprog  { border-color: #F59E0B; color: #FCD34D; }
.tdm-meta-chip.status-done    { border-color: #22C55E; color: #86EFAC; }
.tdm-meta-chip.status-closed  { border-color: #6B7280; color: #9CA3AF; }

/* ── Tabs ── */
.tdm-tabs {
  display: flex;
  gap: 0;
  border-top: 1px solid rgba(255,255,255,.08);
}
.tdm-tab {
  padding: 10px 18px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all .15s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: 'DM Sans', sans-serif;
}
.tdm-tab:hover { color: #D1D5DB; }
.tdm-tab.active { color: #60A5FA; border-bottom-color: #3B82F6; }
.tdm-tab .tdm-tab-badge {
  background: #374151;
  color: #9CA3AF;
  font-size: 0.65rem;
  padding: 1px 6px;
  border-radius: 10px;
  font-family: 'DM Mono', monospace;
}
.tdm-tab.active .tdm-tab-badge { background: #1D4ED8; color: #BFDBFE; }

/* ── Body ── */
.tdm-body {
  flex: 1;
  overflow-y: auto;
  background: #F8FAFC;
}
.tdm-body::-webkit-scrollbar { width: 5px; }
.tdm-body::-webkit-scrollbar-thumb { background: #E5E9EF; border-radius: 3px; }

/* ── Info tab ── */
.tdm-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: #E5E9EF;
  border-bottom: 1px solid #E5E9EF;
}
.tdm-info-cell {
  background: #fff;
  padding: 14px 18px;
}
.tdm-info-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #9CA3AF;
  margin-bottom: 4px;
}
.tdm-info-val {
  font-size: 0.88rem;
  color: #1A1D23;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.tdm-info-body {
  background: #fff;
  padding: 16px 18px;
  margin: 12px;
  border-radius: 10px;
  border: 1px solid #E5E9EF;
  font-size: 0.88rem;
  color: #374151;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

/* ── Replies chat ── */
.tdm-replies {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 300px;
}
.tdm-reply-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}
.tdm-reply-row.right { flex-direction: row-reverse; }
.tdm-reply-avatar {
  width: 30px; height: 30px;
  min-width: 30px; min-height: 30px;
  border-radius: 50%;
  background: #E5E9EF;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #6B7280;
  flex-shrink: 0;
  overflow: hidden;
  line-height: 1;
}
.tdm-reply-row.right .tdm-reply-avatar { background: #DBEAFE; color: #1D4ED8; }
.tdm-reply-bubble {
  max-width: 72%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 0.85rem;
  line-height: 1.5;
  position: relative;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.tdm-reply-row.left  .tdm-reply-bubble { background: #fff; border: 1px solid #E5E9EF; border-bottom-left-radius: 4px; color: #374151; }
.tdm-reply-row.right .tdm-reply-bubble { background: #2563EB; color: #fff; border-bottom-right-radius: 4px; }
.tdm-reply-time {
  font-size: 0.65rem;
  color: #9CA3AF;
  margin-top: 3px;
  text-align: right;
}
.tdm-reply-row.left .tdm-reply-time { text-align: left; }
.tdm-no-replies {
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-size: 0.85rem;
}
.tdm-no-replies i { font-size: 1.8rem; display: block; margin-bottom: 8px; opacity: .3; }

/* ── Reply compose ── */
.tdm-compose {
  background: #fff;
  border-top: 1px solid #E5E9EF;
  padding: 14px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-shrink: 0;
}
.tdm-compose-input {
  flex: 1;
  padding: 10px 14px;
  border: 1.5px solid #E5E9EF;
  border-radius: 10px;
  font-size: 0.85rem;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  resize: none;
  min-height: 44px;
  max-height: 120px;
  line-height: 1.5;
  color: #1A1D23;
  transition: border-color .15s;
}
.tdm-compose-input:focus { border-color: #3B82F6; }
.tdm-compose-send {
  width: 42px; height: 42px;
  border-radius: 10px;
  border: none;
  background: #2563EB;
  color: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.95rem;
  transition: background .15s;
  flex-shrink: 0;
}
.tdm-compose-send:hover { background: #1D4ED8; }
.tdm-compose-send:disabled { background: #BFDBFE; cursor: not-allowed; }

/* ── Assign / Status tabs ── */
.tdm-form-tab {
  padding: 24px;
}
.tdm-form-group {
  margin-bottom: 18px;
}
.tdm-form-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  display: block;
}
.tdm-form-tab .p-dropdown {
  width: 100%;
  border-radius: 10px !important;
  border: 1.5px solid #E5E9EF !important;
  font-size: 0.88rem !important;
}
.tdm-form-tab .p-dropdown:hover { border-color: #3B82F6 !important; }
.tdm-form-note {
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #92400E;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 18px;
}
.tdm-form-submit-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.tdm-btn-primary {
  background: #2563EB !important;
  border: none !important;
  border-radius: 8px !important;
  font-family: 'DM Sans', sans-serif !important;
  font-size: 0.85rem !important;
}
.tdm-btn-cancel {
  background: transparent !important;
  border: 1.5px solid #E5E9EF !important;
  color: #6B7280 !important;
  border-radius: 8px !important;
  font-family: 'DM Sans', sans-serif !important;
  font-size: 0.85rem !important;
}
.tdm-btn-cancel:hover { border-color: #9CA3AF !important; }

/* Status history timeline */
.tdm-timeline {
  padding: 16px 20px;
}
.tdm-tl-item {
  display: flex;
  gap: 12px;
  padding-bottom: 18px;
  position: relative;
}
.tdm-tl-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 13px; top: 26px; bottom: 0;
  width: 1px;
  background: #E5E9EF;
}
.tdm-tl-dot {
  width: 26px; height: 26px;
  border-radius: 50%;
  background: #EFF6FF;
  border: 2px solid #3B82F6;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 0.65rem;
  color: #3B82F6;
}
.tdm-tl-content { flex: 1; }
.tdm-tl-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1A1D23;
}
.tdm-tl-sub {
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 2px;
}
.tdm-tl-date {
  font-size: 0.7rem;
  font-family: 'DM Mono', monospace;
  color: #9CA3AF;
  margin-top: 3px;
}

/* ── Loading spinner ── */
.tdm-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #9CA3AF;
  font-size: 0.85rem;
  gap: 10px;
}
.tdm-spinner i { font-size: 1.2rem; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
`;

const STATUS_COLORS = {
  "Submitted":           "#6C757D",
  "Assigned to Team":    "#FFB200",
  "Employee in the way": "#FFB200",
  "Processing":          "#F57F17",
  "Completed":           "#28A745",
  // fallbacks for other possible names
  "Open":        "#6C757D",
  "In Progress": "#F57F17",
  "Resolved":    "#28A745",
  "Closed":      "#28A745",
};

function getStatusColor(statusName) {
  return STATUS_COLORS[statusName] || "#6C757D";
}

class TicketDetailModal extends Component {
  state = {
    activeTab:     this.props.defaultTab || "replies",
    replies:       [],
    statusLog:     [],
    replyText:     "",
    employeeId:    "",
    statusId:      "",
    employeeOptions: [],
    statusOptions:   [],
    loadingReplies:  true,
    loadingLog:      false,
    submitting:      false,
  };

  componentDidMount() {
    this.loadReplies();
    this.loadDropdowns();
    if (this.state.activeTab === "history") this.loadStatusLog();
  }

  loadReplies() {
    this.setState({ loadingReplies: true });
    post("/api/data/GetReplies", { ticketId: this.props.ticketId }, () => {})
      .then(res => this.setState({ replies: res.data.respData.data || [], loadingReplies: false }))
      .catch(() => this.setState({ loadingReplies: false }));
  }

  loadDropdowns() {
    post("/api/data/GetTicketEmployees", null, () => {})
      .then(res => {
        const list = res.data.respData.data || [];
        // Handle whatever shape the API returns — try common field name patterns
        const employeeOptions = list.map(e => ({
          label: e.EMPLOYEE_NAME || e.label || e.NAME || e.name || String(e.ID || e.id || ""),
          value: e.ID           || e.value || e.id  || e.Id  || 0,
        })).filter(o => o.label);
        this.setState({ employeeOptions });
      }).catch(() => {});

    post("/api/data/GetTicketSatuses", null, () => {})
      .then(res => {
        const list = res.data.respData.data || [];
        const STATUS_COLORS_MAP = {
          "Submitted":        "#6C757D",
          "Assigned to Team": "#FFB200",
          "Employee in the way": "#FFB200",
          "Processing":       "#F57F17",
          "Completed":        "#28A745",
        };
        const statusOptions = list.map(s => ({
          label: s.STATUS_EN || s.label || s.NAME || s.name || String(s.ID || s.id || ""),
          value: s.ID        || s.value || s.id  || s.Id  || 0,
          color: STATUS_COLORS_MAP[s.STATUS_EN] || "#6C757D",
        })).filter(o => o.label);
        this.setState({ statusOptions });
      }).catch(() => {});
  }

  loadStatusLog() {
    this.setState({ loadingLog: true });
    post("/api/data/GetTicketStatusLog", { ticketId: this.props.ticketId }, () => {})
      .then(res => this.setState({ statusLog: res.data.respData.data || [], loadingLog: false }))
      .catch(() => this.setState({ loadingLog: false }));
  }

  switchTab(tab) {
    this.setState({ activeTab: tab });
    if (tab === "history" && this.state.statusLog.length === 0) this.loadStatusLog();
  }

  submitReply() {
    const { replyText } = this.state;
    if (!replyText.trim()) return;
    this.setState({ submitting: true });
    post("/api/data/AddTicketReply", { ticketId: this.props.ticketId, reply: replyText }, () => {})
      .then(res => {
        if (res.data.respCode === 0) {
          this.setState({ replyText: "", submitting: false });
          this.loadReplies();
        } else {
          this.setState({ submitting: false });
        }
      })
      .catch(() => this.setState({ submitting: false }));
  }

  submitAssign() {
    const { employeeId } = this.state;
    if (!employeeId) return;
    this.setState({ submitting: true });
    post("/api/data/AssignEmployee", { ticketId: this.props.ticketId, employeeId }, () => {})
      .then(res => {
        if (res.data.respCode === 0) {
          this.setState({ submitting: false });
          this.props.onClose(true);
        } else {
          this.setState({ submitting: false });
        }
      })
      .catch(() => this.setState({ submitting: false }));
  }

  submitStatus() {
    const { statusId } = this.state;
    if (!statusId) return;
    this.setState({ submitting: true });
    post("/api/data/TicketChangeStatus", { ticketId: this.props.ticketId, statusId }, () => {})
      .then(res => {
        if (res.data.respCode === 0) {
          this.setState({ submitting: false });
          this.props.onClose(true);
        } else {
          this.setState({ submitting: false });
        }
      })
      .catch(() => this.setState({ submitting: false }));
  }

  handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.submitReply();
    }
  }

  getInitials(name) {
    if (!name) return "?";
    return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  }

  // ── Tab renderers ──────────────────────────────────────────────────────────

  renderInfoTab() {
    const { t } = this.props;
    const td = this.props.ticketData;
    if (!td) return null;
    const fields = [
      { label: t("col.owner"),        val: td.OWNER_NAME },
      { label: t("col.unit"),         val: td.UNIT_NAME },
      { label: t("col.compound"),     val: td.COMPOUND_NAME },
      { label: t("col.ticketType"),   val: td.TYPE_NAME },
      { label: t("col.status"),       val: td.STATUS_EN },
      { label: t("col.assignedTo"),   val: td.EMPLOYEE_NAME || t("tick.unassigned") },
      { label: t("col.assignedDate"), val: td.ASSIGN_EMPLOYEE_DATE ? new Date(td.ASSIGN_EMPLOYEE_DATE).toLocaleString() : "—" },
      { label: t("col.created"),      val: td.INSERT_DATE ? new Date(td.INSERT_DATE).toLocaleString() : "—" },
      { label: t("col.lastStatus"),   val: td.LAST_STATUS_DATE ? new Date(td.LAST_STATUS_DATE).toLocaleString() : "—" },
      { label: t("col.lastUpdate"),   val: td.LAST_UPDATE ? new Date(td.LAST_UPDATE).toLocaleString() : "—" },
    ];

    return (
      <div>
        <div className="tdm-info-grid">
          {fields.map((f, i) => (
            <div key={i} className="tdm-info-cell">
              <div className="tdm-info-label">{f.label}</div>
              <div className="tdm-info-val">{f.val || "—"}</div>
            </div>
          ))}
        </div>
        {td.BODY && (
          <div style={{ padding: "0 0 0 0" }}>
            <div style={{ padding: "14px 18px 4px", fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "#9CA3AF" }}>
              {t("col.description")}
            </div>
            <div className="tdm-info-body">{td.BODY}</div>
          </div>
        )}
      </div>
    );
  }

  renderRepliesTab() {
    const { t } = this.props;
    const { replies, replyText, loadingReplies, submitting } = this.state;

    return (
      <>
        <div className="tdm-replies">
          {loadingReplies ? (
            <div className="tdm-spinner">
              <i className="fa fa-circle-o-notch" /> {t("common.loading")}
            </div>
          ) : replies.length === 0 ? (
            <div className="tdm-no-replies">
              <i className="fa fa-comments-o" />
              {t("tick.noRepliesYet")}
            </div>
          ) : (
            replies.map((r, i) => {
              const isSystem = r.ENTITY_TYPE !== "SysUser";
              return (
                <div key={i} className={`tdm-reply-row ${isSystem ? "right" : "left"}`}>
                  <div className="tdm-reply-avatar">
                    {isSystem ? <i className="fa fa-headset" style={{ fontSize: "0.75rem" }} /> : this.getInitials(r.ENTITY_NAME || "U")}
                  </div>
                  <div>
                    <div className="tdm-reply-bubble">{r.REPLY}</div>
                    <div className="tdm-reply-time">
                      {r.INSERT_DATE ? new Date(r.INSERT_DATE).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {sysJwt.isAuthorized(["CB3F"]) && (
          <div className="tdm-compose">
            <textarea
              className="tdm-compose-input"
              placeholder={t("tick.typeReplyPlaceholder")}
              value={replyText}
              onChange={e => this.setState({ replyText: e.target.value })}
              onKeyDown={e => this.handleKeyDown(e)}
              rows={1}
            />
            <button
              className="tdm-compose-send"
              disabled={!replyText.trim() || submitting}
              onClick={() => this.submitReply()}
            >
              <i className={submitting ? "fa fa-circle-o-notch fa-spin" : "fa fa-paper-plane"} />
            </button>
          </div>
        )}
      </>
    );
  }

  renderAssignTab() {
    const { t } = this.props;
    const { employeeId, employeeOptions, submitting } = this.state;
    const td = this.props.ticketData;
    if (!sysJwt.isAuthorized(["CB7C"])) return (
      <div className="tdm-form-tab" style={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
        {t("tick.notAuthorizedAssign")}
      </div>
    );

    return (
      <div className="tdm-form-tab">
        {td?.EMPLOYEE_NAME && (
          <div className="tdm-form-note">
            <i className="fa fa-info-circle" style={{ marginTop: 2 }} />
            <span>{t("tick.currentlyAssignedTo")} <strong>{td.EMPLOYEE_NAME}</strong>. {t("tick.reassignNote")}</span>
          </div>
        )}
        {td?.NEED_EMPLOYEE === false && (
          <div className="tdm-form-note" style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" }}>
            <i className="fa fa-warning" style={{ marginTop: 2 }} />
            <span>{t("tick.noEmployeeRequired")}</span>
          </div>
        )}
        <div className="tdm-form-group">
          <label className="tdm-form-label">{t("tick.selectEmployee")}</label>
          <Dropdown
            value={employeeId}
            options={employeeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={e => this.setState({ employeeId: e.value })}
            placeholder={t("tick.chooseEmployee")}
            filter
            filterPlaceholder={t("tick.searchEmployees")}
            appendTo={document.body}
          />
        </div>
        <div className="tdm-form-submit-row">
          <Button
            label={t("btn.cancel")}
            className="tdm-btn-cancel p-button-rounded"
            onClick={() => this.props.onClose(false)}
          />
          <Button
            label={submitting ? t("tick.assigning") : t("tick.assignEmployee")}
            icon={submitting ? "fa fa-circle-o-notch fa-spin" : "fa fa-user-plus"}
            className="tdm-btn-primary p-button-rounded"
            disabled={!employeeId || submitting}
            onClick={() => this.submitAssign()}
          />
        </div>
      </div>
    );
  }

  renderStatusTab() {
    const { t } = this.props;
    const { statusId, statusOptions, submitting } = this.state;
    const td = this.props.ticketData;
    if (!sysJwt.isAuthorized(["CB7D"])) return (
      <div className="tdm-form-tab" style={{ color: "#9CA3AF", fontSize: "0.85rem" }}>
        {t("tick.notAuthorizedStatus")}
      </div>
    );

    return (
      <div className="tdm-form-tab">
        {td?.STATUS_EN && (
          <div className="tdm-form-note">
            <i className="fa fa-info-circle" style={{ marginTop: 2 }} />
            <span>{t("tick.currentStatus")} <strong>{td.STATUS_EN}</strong>. {t("tick.statusChangeNote")}</span>
          </div>
        )}
        <div className="tdm-form-group">
          <label className="tdm-form-label">{t("tick.newStatus")}</label>
          <Dropdown
            value={statusId}
            options={statusOptions}
            optionLabel="label"
            optionValue="value"
            onChange={e => this.setState({ statusId: e.value })}
            placeholder={t("tick.chooseStatus")}
            appendTo={document.body}
            itemTemplate={(option) => (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: option.color || "#6C757D",
                  flexShrink: 0, display: "inline-block",
                }} />
                <span>{option.label}</span>
              </div>
            )}
            valueTemplate={(option) => option ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: option.color || "#6C757D",
                  flexShrink: 0, display: "inline-block",
                }} />
                <span>{option.label}</span>
              </div>
            ) : <span style={{ color: "#9CA3AF" }}>{t("tick.chooseStatus")}</span>}
          />
        </div>
        <div className="tdm-form-submit-row">
          <Button
            label={t("btn.cancel")}
            className="tdm-btn-cancel p-button-rounded"
            onClick={() => this.props.onClose(false)}
          />
          <Button
            label={submitting ? t("tick.saving") : t("tick.changeStatus")}
            icon={submitting ? "fa fa-circle-o-notch fa-spin" : "fa fa-exchange"}
            className="tdm-btn-primary p-button-rounded"
            disabled={!statusId || submitting}
            onClick={() => this.submitStatus()}
          />
        </div>
      </div>
    );
  }

  renderHistoryTab() {
    const { t } = this.props;
    const { statusLog, loadingLog } = this.state;
    if (loadingLog) return <div className="tdm-spinner"><i className="fa fa-circle-o-notch" /> {t("common.loading")}</div>;
    if (!statusLog.length) return (
      <div className="tdm-timeline">
        <div style={{ color: "#9CA3AF", fontSize: "0.85rem", textAlign: "center", padding: "30px 0" }}>
          {t("tick.noStatusHistory")}
        </div>
      </div>
    );

    return (
      <div className="tdm-timeline">
        {statusLog.map((entry, i) => (
          <div key={i} className="tdm-tl-item">
            <div className="tdm-tl-dot"><i className="fa fa-exchange" /></div>
            <div className="tdm-tl-content">
              <div className="tdm-tl-title">
                {entry.OLD_STATUS_EN
                  ? <>{entry.OLD_STATUS_EN} → {entry.NEW_STATUS_EN}</>
                  : <>Set to {entry.NEW_STATUS_EN}</>
                }
              </div>
              {entry.CHANGED_BY_NAME && (
                <div className="tdm-tl-sub">by {entry.CHANGED_BY_NAME}</div>
              )}
              <div className="tdm-tl-date">
                {entry.CHANGE_DATE ? new Date(entry.CHANGE_DATE).toLocaleString() : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const { activeTab, replies } = this.state;
    const td = this.props.ticketData;
    const statusColor = td ? getStatusColor(td.STATUS_EN) : "#6C757D";

    const tabs = [
      { key: "replies", label: t("tick.conversation"), icon: "fa fa-comments", badge: replies.length || null },
      { key: "info",    label: t("col.details"),        icon: "fa fa-info-circle" },
      { key: "assign",  label: t("tick.assign"),        icon: "fa fa-user-plus",  hide: !sysJwt.isAuthorized(["CB7C"]) },
      { key: "status",  label: t("col.status"),         icon: "fa fa-exchange",   hide: !sysJwt.isAuthorized(["CB7D"]) },
      { key: "history", label: t("tick.history"),       icon: "fa fa-history" },
    ].filter(tb => !tb.hide);

    const content = (
      <>
        <style>{styles}</style>
        <div
          className="tdm-overlay"
          onClick={e => { if (e.target === e.currentTarget) this.props.onClose(false); }}
        >
          <div className="tdm-modal">
            {/* Header */}
            <div className="tdm-header">
              <div className="tdm-header-top">
                <div>
                  <div className="tdm-ticket-id">Ticket #{td?.ID}</div>
                  <div className="tdm-ticket-title">{td?.TITLE}</div>
                </div>
                <button className="tdm-close-btn" onClick={() => this.props.onClose(false)}>
                  <i className="fa fa-times" />
                </button>
              </div>

              {/* Meta chips */}
              <div className="tdm-meta">
                {td?.STATUS_EN && (
                  <span className="tdm-meta-chip" style={{
                    borderColor: statusColor,
                    color: statusColor,
                    background: `${statusColor}18`,
                  }}>
                    <span style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: statusColor, display: "inline-block", flexShrink: 0,
                    }} />
                    {td.STATUS_EN}
                  </span>
                )}
                {td?.TYPE_NAME && (
                  <span className="tdm-meta-chip">
                    <i className="fa fa-tag" /> {td.TYPE_NAME}
                  </span>
                )}
                {td?.OWNER_NAME && (
                  <span className="tdm-meta-chip">
                    <i className="fa fa-user" /> {td.OWNER_NAME}
                  </span>
                )}
                {td?.UNIT_NAME && (
                  <span className="tdm-meta-chip">
                    <i className="fa fa-home" /> {td.UNIT_NAME}
                  </span>
                )}
                {td?.COMPOUND_NAME && (
                  <span className="tdm-meta-chip">
                    <i className="fa fa-building" /> {td.COMPOUND_NAME}
                  </span>
                )}
                {td?.EMPLOYEE_NAME
                  ? <span className="tdm-meta-chip"><i className="fa fa-wrench" /> {td.EMPLOYEE_NAME}</span>
                  : <span className="tdm-meta-chip" style={{ borderColor: "#EF4444", color: "#FCA5A5" }}><i className="fa fa-user-times" /> {t("tick.unassigned")}</span>
                }
              </div>

              {/* Tabs */}
              <div className="tdm-tabs">
                {tabs.map(tb => (
                  <button
                    key={tb.key}
                    className={`tdm-tab ${activeTab === tb.key ? "active" : ""}`}
                    onClick={() => this.switchTab(tb.key)}
                  >
                    <i className={tb.icon} />
                    {tb.label}
                    {tb.badge > 0 && <span className="tdm-tab-badge">{tb.badge}</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="tdm-body">
              {activeTab === "replies" && this.renderRepliesTab()}
              {activeTab === "info"    && this.renderInfoTab()}
              {activeTab === "assign"  && this.renderAssignTab()}
              {activeTab === "status"  && this.renderStatusTab()}
              {activeTab === "history" && this.renderHistoryTab()}
            </div>
          </div>
        </div>
      </>
    );

    return ReactDOM.createPortal(content, document.body);
  }
}

export default withTranslation()(TicketDetailModal);
